import { useEffect, useRef, useState, useCallback } from "react";
import type { ClientMessage, ServerMessage } from "@shared/types";

export type SocketStatus = "connecting" | "open" | "closed";

interface Options {
  onMessage: (msg: ServerMessage) => void;
}

// Robust WebSocket hook with auto-reconnect + heartbeat.
export function useSessionSocket(sessionId: string | null, { onMessage }: Options) {
  const [status, setStatus] = useState<SocketStatus>("connecting");
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);

  onMessageRef.current = onMessage;

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    closedByUs.current = false;

    const connect = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${location.host}/api/sessions/${sessionId}/ws`);
      wsRef.current = ws;
      setStatus("connecting");

      ws.onopen = () => setStatus("open");
      ws.onmessage = (e) => {
        try {
          onMessageRef.current(JSON.parse(e.data) as ServerMessage);
        } catch {
          /* ignore malformed */
        }
      };
      ws.onclose = () => {
        setStatus("closed");
        if (!closedByUs.current) {
          reconnectRef.current = setTimeout(connect, 1500);
        }
      };
      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      closedByUs.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [sessionId]);

  return { status, send };
}
